import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { appDate, appDateTime } from '../../../../utils/appDateFormat';

const StatusTimeline = ({ 
  request, 
  onClose, 
  onAcceptRequest, 
  onRejectRequest, 
  userRole 
}) => {
  const { t } = useTranslation();
  const [showActionModal, setShowActionModal] = useState(null); // 'accept' or 'reject'
  const [actionComment, setActionComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  if (!request) return null;

  const normalizeActorLabel = (actor) => {
    const label = actor && String(actor).trim().length > 0 ? String(actor).trim() : null;
    if (!label) return null;

    // Guard against legacy placeholders from older API responses.
    const lower = label.toLowerCase();
    if (lower === 'employee' || lower === 'approver' || lower === 'hr team' || lower === 'auto-applied') {
      return null;
    }

    return label;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return t('Pending');
    
    return appDateTime(new Date(dateString));
  };

  const getStageIcon = (stage, status) => {
    if (status === 'completed') {
      return 'CheckCircle';
    } else if (status === 'rejected') {
      return 'XCircle';
    } else if (status === 'pending') {
      return 'Clock';
    }
    return 'Circle';
  };

  const getStageColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'rejected':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'pending':
        return 'text-orange-600 bg-orange-100 border-orange-200';
      default:
        return 'text-gray-400 bg-gray-100 border-border';
    }
  };

  const getStageTitle = (stage) => {
    switch (stage) {
      case 'submitted':
        return 'Request Submitted';
      case 'manager_review':
        return 'Manager Review';
      case 'hr_approval':
        return 'HR Approval';
      default:
        return stage?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
    }
  };

  const getStageDescription = (stage, status, actor) => {
    const actorLabel = normalizeActorLabel(actor);

    if (status === 'completed') {
      switch (stage) {
        case 'submitted':
          return actorLabel ? `Request submitted by ${actorLabel}` : 'Request submitted';
        case 'manager_review':
          return actorLabel ? `Reviewed and approved by ${actorLabel}` : 'Reviewed and approved';
        case 'hr_approval':
          return actorLabel ? `Final approval granted by ${actorLabel}` : 'Final approval granted';
        default:
          return actorLabel ? `Completed by ${actorLabel}` : 'Completed';
      }
    } else if (status === 'rejected') {
      return actorLabel ? `Request rejected by ${actorLabel}` : 'Request rejected';
    } else if (status === 'pending') {
      switch (stage) {
        case 'manager_review':
          return actorLabel ? `Waiting for manager approval from ${actorLabel}` : 'Waiting for manager approval';
        case 'hr_approval':
          return actorLabel ? `Waiting for HR approval from ${actorLabel}` : 'Waiting for HR approval';
        default:
          return actorLabel ? `Pending action from ${actorLabel}` : 'Pending action';
      }
    }
    return 'Not started';
  };

  const getTypeLabel = (type) => {
    const types = {
      'missing-clockout': 'Missing Clock Out', 
      'incorrect-time': 'Incorrect Time',
      'missing-entry': 'Missing Entry',
      'break-time': 'Break Time Correction'
    };
    return types?.[type] || type;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-border';
    }
  };

  const handleAccept = () => {
    onAcceptRequest?.(request, actionComment);
    setShowActionModal(null);
    setActionComment('');
    onClose?.();
  };

  const handleReject = () => {
    if (!rejectionReason?.trim()) return;
    onRejectRequest?.(request, rejectionReason, actionComment);
    setShowActionModal(null);
    setActionComment('');
    setRejectionReason('');
    onClose?.();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground flex items-center">
                <Icon name="Timeline" size={20} className="mr-2" />
                {t('Request Timeline')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('Track the progress of correction request {{id}}', { id: request?.id })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Request Summary */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">{request?.id}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(request?.status)}`}>
                  {request?.status?.toUpperCase()}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('Type:', { nsSeparator: false })}</span>
                  <span className="ml-2 font-medium text-foreground">{getTypeLabel(request?.type)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('Date:', { nsSeparator: false })}</span>
                  <span className="ml-2 font-medium text-foreground">{request?.date ? appDate(new Date(request.date)) : t('Pending')}</span>
                </div>
              </div>

              {/* Time Changes */}
              {(request?.requestedCheckIn || request?.requestedCheckOut) && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">{t('Requested Changes:', { nsSeparator: false })}</p>
                  {request?.requestedCheckIn && (
                    <div className="flex items-center justify-between text-sm bg-card p-2 rounded">
                      <span className="text-muted-foreground">{t('Check In:', { nsSeparator: false })}</span>
                      <span className="font-mono">
                        {request?.currentCheckIn ? (
                          <>
                            <span className="text-red-600 line-through">{request?.currentCheckIn}</span>
                            <span className="mx-2">→</span>
                            <span className="text-green-600">{request?.requestedCheckIn}</span>
                          </>
                        ) : (
                          <span className="text-green-600">{request?.requestedCheckIn}</span>
                        )}
                      </span>
                    </div>
                  )}
                  {request?.requestedCheckOut && (
                    <div className="flex items-center justify-between text-sm bg-card p-2 rounded">
                      <span className="text-muted-foreground">{t('Check Out:', { nsSeparator: false })}</span>
                      <span className="font-mono">
                        {request?.currentCheckOut ? (
                          <>
                            <span className="text-red-600 line-through">{request?.currentCheckOut}</span>
                            <span className="mx-2">→</span>
                            <span className="text-green-600">{request?.requestedCheckOut}</span>
                          </>
                        ) : (
                          <span className="text-green-600">{request?.requestedCheckOut}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Reason */}
              <div>
                <p className="text-sm font-medium text-foreground">Reason:</p>
                <p className="text-sm text-muted-foreground mt-1 bg-card p-2 rounded">
                  {request?.reason}
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-foreground">Approval Timeline</h4>
              
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
                
                <div className="space-y-6">
                  {request?.workflow?.map((stage, index) => (
                    <div key={index} className="relative flex items-start space-x-4">
                      {/* Timeline Icon */}
                      <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 ${getStageColor(stage?.status)}`}>
                        <Icon name={getStageIcon(stage?.stage, stage?.status)} size={20} />
                      </div>
                      
                      {/* Timeline Content */}
                      <div className="flex-1 min-w-0 pb-8">
                        <div className="flex items-center justify-between">
                          <h5 className="text-base font-semibold text-foreground">
                            {getStageTitle(stage?.stage)}
                          </h5>
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(stage?.date)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mt-1">
                          {getStageDescription(stage?.stage, stage?.status, stage?.actor)}
                        </p>

                        {/* Show additional info for completed or rejected stages */}
                        {(stage?.status === 'completed' || stage?.status === 'rejected') && (
                          <div className="mt-2 text-sm">
                            <div className="flex items-center space-x-2">
                              <Icon name="User" size={14} className="text-muted-foreground" />
                              <span className="text-muted-foreground">By:</span>
                              <span className="font-medium text-foreground">{normalizeActorLabel(stage?.actor) || '—'}</span>
                            </div>
                          </div>
                        )}

                        {/* Show pending stage estimated time */}
                        {stage?.status === 'pending' && (
                          <div className="mt-2 text-sm">
                            <div className="flex items-center space-x-2 text-orange-600">
                              <Icon name="Clock" size={14} />
                              <span>Estimated time: 1-2 business days</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Comments Section */}
            {request?.comments?.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">Comments</h4>
                
                <div className="space-y-3">
                  {request?.comments?.map((comment, index) => (
                    <div key={index} className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-foreground">{comment?.author}</span>
                        <span className="text-sm text-muted-foreground">{formatDateTime(comment?.date)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{comment?.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {request?.attachments?.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">Attachments</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {request?.attachments?.map((attachment, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                      <Icon name="File" size={16} className="text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{attachment}</p>
                        <p className="text-xs text-muted-foreground">Click to download</p>
                      </div>
                      <Icon name="Download" size={14} className="text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {request?.status === 'rejected' && request?.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-base font-semibold text-red-900 mb-2 flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-2" />
                  Rejection Reason
                </h4>
                <p className="text-sm text-red-800">{request?.rejectionReason}</p>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-card border-t border-border p-6 flex justify-between">
            <div className="flex space-x-3">
              {request?.status === 'pending' && (userRole === 'manager' || userRole === 'hr') && (
                <>
                  <button
                    onClick={() => setShowActionModal('accept')}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  >
                    <Icon name="Check" size={16} className="mr-2" />
                    {t('Accept Request')}
                  </button>
                  <button
                    onClick={() => setShowActionModal('reject')}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                  >
                    <Icon name="X" size={16} className="mr-2" />
                    {t('Reject Request')}
                  </button>
                </>
              )}
              
              {request?.status === 'rejected' && (
                <button className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors">
                  {t('Resubmit Request')}
                </button>
              )}
            </div>
            
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
            >
              {t('Close')}
            </button>
          </div>
        </div>
      </div>
      {/* Accept Modal */}
      {showActionModal === 'accept' && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="CheckCircle" size={20} className="mr-2 text-green-600" />
                {t('Accept Request')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('Accept correction request {{id}}', { id: request?.id })}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('Comment (Optional)')}
                </label>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e?.target?.value)}
                  placeholder={t('Add a comment about this approval...')}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm text-foreground bg-background resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="p-6 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowActionModal(null);
                  setActionComment('');
                }}
                className="px-4 py-2 text-sm font-medium text-muted-foreground bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleAccept}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                {t('Accept Request')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reject Modal */}
      {showActionModal === 'reject' && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="XCircle" size={20} className="mr-2 text-red-600" />
                {t('Reject Request')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('Reject correction request {{id}}', { id: request?.id })}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('Rejection Reason *')}
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  placeholder={t('Please provide a reason for rejecting this request...')}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm text-foreground bg-background resize-none"
                  rows={3}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('Additional Comment (Optional)')}
                </label>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e?.target?.value)}
                  placeholder={t('Add additional comments...')}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm text-foreground bg-background resize-none"
                  rows={2}
                />
              </div>
            </div>

            <div className="p-6 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowActionModal(null);
                  setActionComment('');
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-muted-foreground bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason?.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('Reject Request')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StatusTimeline;